import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ExternalLink, GitCommit, X } from 'lucide-react';
import { TenantMultiSelect, type TenantOption } from '@/shared/components/TenantMultiSelect.tsx';
import { DateTimePicker } from '@/shared/components/DateTimePicker.tsx';
import type { ApproveDeployDraftDTO } from '../api/deploy-drafts.api.ts';
import type { DeployDraftRow } from '@/shared/types/deploy.ts';

/* ── schema ───────────────────────────────────────────── */

const schema = z
  .object({
    titulo: z.string().min(2, 'Título deve ter no mínimo 2 caracteres'),
    iniciadoEm: z.string().min(1, 'Data de início é obrigatória'),
    encerradoEm: z.string().min(1, 'Data de término é obrigatória'),
  })
  .refine((d) => new Date(d.encerradoEm) > new Date(d.iniciadoEm), {
    message: 'Data de término deve ser posterior à data de início',
    path: ['encerradoEm'],
  });

type FormFields = z.infer<typeof schema>;

/* ── props ────────────────────────────────────────────── */

interface Props {
  draft: DeployDraftRow;
  tenants: TenantOption[];
  onClose: () => void;
  onApprove: (input: ApproveDeployDraftDTO) => void;
  onReject: (motivo: string) => void;
  isSubmitting: boolean;
}

/* ── componente ───────────────────────────────────────── */

/**
 * Última barreira antes do SLA: nada do rascunho entra no cálculo até esta
 * tela ser confirmada. Tudo vem pré-preenchido pelo webhook e é editável.
 */
export function DeployDraftReviewModal({
  draft,
  tenants,
  onClose,
  onApprove,
  onReject,
  isSubmitting,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>(draft.tenants.map((t) => t.tenantId));
  const [tenantError, setTenantError] = useState('');
  const [rejeitando, setRejeitando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      titulo: draft.tituloSugerido,
      iniciadoEm: draft.iniciadoEm,
      encerradoEm: draft.encerradoEm,
    },
  });

  const [iniciadoEmDate, setIniciadoEmDate] = useState<Date | null>(new Date(draft.iniciadoEm));
  const [encerradoEmDate, setEncerradoEmDate] = useState<Date | null>(new Date(draft.encerradoEm));

  function handleIniciadoEmChange(d: Date | null) {
    setIniciadoEmDate(d);
    setValue('iniciadoEm', d ? d.toISOString() : '', { shouldValidate: true });
  }

  function handleEncerradoEmChange(d: Date | null) {
    setEncerradoEmDate(d);
    setValue('encerradoEm', d ? d.toISOString() : '', { shouldValidate: true });
  }

  function handleToggle(id: string) {
    setTenantError('');
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function onFormSubmit(data: FormFields) {
    if (selectedIds.length === 0) {
      setTenantError('Selecione ao menos um tenant');
      return;
    }

    onApprove({
      titulo: data.titulo,
      iniciadoEm: new Date(data.iniciadoEm).toISOString(),
      encerradoEm: new Date(data.encerradoEm).toISOString(),
      tenantIds: selectedIds,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold">Revisar Deploy</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5"
        >
          {/* Origem — somente leitura */}
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs dark:border-gray-700 dark:bg-gray-800/60">
            <div className="font-mono text-gray-700 dark:text-gray-300">
              {draft.workspace}/{draft.repoSlug} · {draft.branch}
            </div>
            {draft.commitMensagem && (
              <div className="mt-1 flex items-start gap-1.5 text-gray-500">
                <GitCommit size={13} className="mt-0.5 shrink-0" />
                <span className="line-clamp-2">{draft.commitMensagem}</span>
              </div>
            )}
            {draft.pipelineUrl && (
              <a
                href={draft.pipelineUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
              >
                Ver pipeline <ExternalLink size={12} />
              </a>
            )}
          </div>

          {/* Título */}
          <div>
            <label className="mb-1 block text-sm font-medium">Título</label>
            <input
              {...register('titulo')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
            />
            {errors.titulo && <p className="mt-1 text-xs text-red-500">{errors.titulo.message}</p>}
          </div>

          {/* Janela */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Início</label>
              <DateTimePicker
                value={iniciadoEmDate}
                onChange={handleIniciadoEmChange}
                maxDate={encerradoEmDate ?? undefined}
                placeholder="dd/mm/aaaa hh:mm"
              />
              {errors.iniciadoEm && (
                <p className="mt-1 text-xs text-red-500">{errors.iniciadoEm.message}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Término</label>
              <DateTimePicker
                value={encerradoEmDate}
                onChange={handleEncerradoEmChange}
                minDate={iniciadoEmDate ?? undefined}
                placeholder="dd/mm/aaaa hh:mm"
              />
              {errors.encerradoEm && (
                <p className="mt-1 text-xs text-red-500">{errors.encerradoEm.message}</p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Este período será excluído do cálculo de SLA dos tenants selecionados.
          </p>

          <TenantMultiSelect
            tenants={tenants}
            selected={selectedIds}
            selectAll={false}
            onToggle={handleToggle}
            onSelectAll={() => {
              setTenantError('');
              setSelectedIds(tenants.map((t) => t.id));
            }}
            onClearAll={() => setSelectedIds([])}
          />
          {tenantError && <p className="text-xs text-red-500">{tenantError}</p>}

          {/* Rejeição */}
          {rejeitando && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3 dark:border-red-900/50 dark:bg-red-900/20">
              <label className="mb-1 block text-sm font-medium">Motivo da rejeição</label>
              <input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                autoFocus
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-800"
                placeholder="Ex.: deploy sem indisponibilidade"
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRejeitando(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  disabled={motivo.trim().length < 3 || isSubmitting}
                  onClick={() => onReject(motivo.trim())}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Confirmar rejeição
                </button>
              </div>
            </div>
          )}

          {/* Ações */}
          {!rejeitando && (
            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setRejeitando(true)}
                className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                Rejeitar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Aprovando...' : 'Aprovar e criar manutenção'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
